import { api } from '../api';
import { useLoad } from '../hooks';
import { duration, eventLabel, stamp, timeAgo } from '../format';
import {
  BudgetLine,
  Empty,
  ErrorNote,
  HealthBar,
  Loading,
  StatusPill,
  Table,
  UsageBar,
} from './ui';
import type { ResourceDetail, ResourceEvent, ResourceSummary } from '../types';

/**
 * Platformun bir kaynak hakkında bildiği her şey — neden o durumda olduğu dahil.
 * Asıl mesele olay günlüğü: doğrulama bekleyen bir hesap, ancak hangi çalışmanın ve ne
 * zaman onu o hale getirdiği görülebiliyorsa aksiyona dönüşür.
 */
export function ResourceDrawer({
  resource,
  onClose,
}: {
  resource: ResourceSummary;
  onClose: () => void;
}) {
  const detail = useLoad(
    () => api.get<ResourceDetail>(`/v1/resources/${resource.id}`),
    [resource.id],
  );
  // Bu uç düz dizi döndürüyor — sarmalı varsaymak olay günlüğünü sessizce boş bırakırdı.
  const events = useLoad(
    () => api.get<ResourceEvent[]>(`/v1/resources/${resource.id}/events?limit=50`),
    [resource.id],
  );

  const usage = detail.data?.usage;
  const budget = detail.data?.budget ?? resource.budget;
  const rows = events.data ?? [];

  return (
    <div className="modal-backdrop right" onClick={onClose}>
      <aside className="drawer" onClick={(e) => e.stopPropagation()}>
        <header className="drawer-head">
          <div>
            <h3>{resource.name}</h3>
            <p className="muted small">
              {resource.type === 'IDENTITY' ? 'Kimlik' : 'Proxy'} · {resource.provider} ·{' '}
              <span className="mono">{resource.id}</span>
            </p>
          </div>
          <button type="button" className="ghost" onClick={onClose}>
            Kapat
          </button>
        </header>

        {detail.error && (
          <div style={{ padding: '14px 16px 0' }}>
            <ErrorNote message={detail.error} />
          </div>
        )}
        {detail.loading && !detail.data && <Loading />}

        <dl className="kv">
          <dt>Durum</dt>
          <dd>
            <StatusPill status={resource.status} />
          </dd>
          <dt>Sağlık</dt>
          <dd>
            <HealthBar score={resource.health.healthScore} />
          </dd>
          <dt>Eşzamanlılık</dt>
          <dd>
            {usage
              ? `${resource.maxConcurrentLeases} içinde ${usage.activeLeases}`
              : `en fazla ${resource.maxConcurrentLeases}`}
          </dd>
          <dt>Bütçe</dt>
          <dd>
            <BudgetLine budget={budget} />
          </dd>
          {usage && (
            <>
              <dt>Çalışma (1 sa)</dt>
              <dd>
                <UsageBar used={usage.runs1h} limit={budget.maxRunsPerHour} />
              </dd>
              <dt>Çalışma (24 sa)</dt>
              <dd>
                <UsageBar used={usage.runs24h} limit={budget.maxRunsPerDay} />
              </dd>
              <dt>Instagram isteği (1 sa)</dt>
              <dd>
                <UsageBar used={usage.upstream1h} limit={budget.maxUpstreamRequestsPerHour} />
              </dd>
              <dt>Instagram isteği (24 sa)</dt>
              <dd>
                <UsageBar used={usage.upstream24h} limit={budget.maxUpstreamRequestsPerDay} />
              </dd>
              <dt>Başarı oranı (24 sa)</dt>
              <dd>
                {usage.successRate24h === null
                  ? '— henüz ölçüm yok'
                  : `%${Math.round(usage.successRate24h * 100)}`}
              </dd>
              <dt>Ortalama süre</dt>
              <dd>{duration(usage.avgDurationMs)}</dd>
            </>
          )}
          <dt>Son başarı</dt>
          <dd title={stamp(resource.health.lastSuccessAt)}>
            {timeAgo(resource.health.lastSuccessAt)}
          </dd>
          <dt>Son hata</dt>
          <dd title={stamp(resource.health.lastFailureAt)}>
            {timeAgo(resource.health.lastFailureAt)}
            {resource.health.lastErrorCode && (
              <span className="muted mono"> · {resource.health.lastErrorCode}</span>
            )}
          </dd>
          {resource.health.cooldownUntil && (
            <>
              <dt>Dinlenme bitişi</dt>
              <dd>{stamp(resource.health.cooldownUntil)}</dd>
            </>
          )}
        </dl>

        <h4>Olay günlüğü</h4>
        {events.error && (
          <div style={{ padding: '0 16px' }}>
            <ErrorNote message={events.error} />
          </div>
        )}
        {rows.length === 0 && !events.loading && <Empty title="Henüz kayıt yok" />}
        {rows.length > 0 && (
          <Table head={['Ne zaman', 'Olay', 'Ayrıntı']}>
            {rows.map((event) => (
              <tr key={event.id}>
                <td className="muted" title={stamp(event.createdAt)}>
                  {timeAgo(event.createdAt)}
                </td>
                <td className="cell-title">{eventLabel(event.type)}</td>
                <td className="muted">{event.message ?? '—'}</td>
              </tr>
            ))}
          </Table>
        )}
      </aside>
    </div>
  );
}
